import {
  toLineupEntry,
  toLineupPlayerName,
  toSideType,
} from "@/pages/TaggingV2/Scorekeeping/api/omnibusMappers";
import type { TeamSide } from "@/pages/TaggingV2/Scorekeeping/constants/teams";
import type { LineUpPlayer } from "@/pages/TaggingV2/Scorekeeping/types/gameFull";
import type { GameLineupPlayer } from "@/types/game";

export type PlayerOption = {
  value: string;
  label: string;
  id: string;
};

export type PlayerField = "pitcher" | "hitter";

export const UNKNOWN_PLAYER_OPTION: PlayerOption = {
  value: "Unknown",
  label: "Unknown",
  id: "unknown",
};

/**
 * Roster players as options. The value is the stored "Last, First" name both
 * taggers write into tags, not the roster's "First Last" display name, so a
 * save writes the same name and existing tags still match their option.
 */
export const rosterToOptions = (players: LineUpPlayer[]): PlayerOption[] =>
  players.map((player) => {
    const name = toLineupPlayerName(player);
    return { value: name, label: name, id: player.playerId };
  });

/**
 * A roster player's tag fields, written as V2 writes them. A changed tag
 * would otherwise keep the previous player's sides and jersey number, and
 * omnibus only matches a tagged player to an existing one when the sides
 * agree, so a stale side can create a duplicate player. The tag keeps its
 * position (the hitter's batting slot).
 */
export const toTaggedPlayerFields = (player: LineUpPlayer) => {
  const { externalId, playerName, hitSide, pitchSide, jerseyNumber } =
    toLineupEntry(player, "pitcher");
  return { externalId, playerName, hitSide, pitchSide, jerseyNumber };
};

/** A game player's option id; an entry without an external id goes by name. */
export const toGamePlayerId = (player: GameLineupPlayer) =>
  player.external_id || `player_${player.player_name}`;

/**
 * A game player's tag fields, from their lineup or substitution entry, for a
 * player no roster has. Like toTaggedPlayerFields, every field is replaced so
 * the tag keeps none of the previous player's. An entry without an external id
 * writes none: its option id is made up, not a player omnibus knows.
 */
export const toGamePlayerFields = (player: GameLineupPlayer) => ({
  externalId: player.external_id || undefined,
  playerName: player.player_name,
  hitSide: toSideType(player.hit_side),
  pitchSide: toSideType(player.pitch_side),
  jerseyNumber: player.jersey_number ?? undefined,
});

export const lineupPlayersToOptions = (
  players: GameLineupPlayer[],
): PlayerOption[] =>
  players.flatMap((player) =>
    player.player_name
      ? [
          {
            value: player.player_name,
            label: player.player_name,
            id: toGamePlayerId(player),
          },
        ]
      : [],
  );

/** The team batting (hitter) or pitching (pitcher) in that half-inning. */
export const getLikelySide = (
  field: PlayerField,
  half: "top" | "bottom" | undefined,
): TeamSide => {
  const battingSide = half === "top" ? "away" : "home";
  if (field === "hitter") return battingSide;
  return battingSide === "away" ? "home" : "away";
};

/**
 * Unknown, then `first`'s roster, the other roster, and the game's lineup and
 * substitution players no roster has (guests, players since removed). Game
 * players carry no team, so they come last; without rosters they're the
 * whole list.
 */
export const orderPlayerOptions = ({
  first,
  second,
  gamePlayers,
}: {
  first: PlayerOption[];
  second: PlayerOption[];
  gamePlayers: PlayerOption[];
}): PlayerOption[] => {
  const ids = new Set<string>([UNKNOWN_PLAYER_OPTION.id]);
  const options = [UNKNOWN_PLAYER_OPTION];
  for (const option of [...first, ...second]) {
    if (ids.has(option.id)) continue;
    ids.add(option.id);
    options.push(option);
  }
  // A lineup entry without an external id gets a name-based id, so match
  // game players on name too
  const names = new Set(options.map((option) => option.value));
  for (const option of gamePlayers) {
    if (ids.has(option.id) || names.has(option.value)) continue;
    ids.add(option.id);
    names.add(option.value);
    options.push(option);
  }
  return options;
};
